import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, isRef as _isRef } from "vue"

const _hoisted_1 = { class: "space-y-5" }
const _hoisted_2 = { class: "space-y-5" }
const _hoisted_3 = { class: "flex flex-wrap justify-end items-center gap-3" }
const _hoisted_4 = {
  key: 0,
  class: "flex gap-2 items-center"
}
const _hoisted_5 = {
  key: 1,
  class: "flex gap-2 items-center"
}
const _hoisted_6 = { class: "flex items-center gap-2" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "flex justify-end" }

import { computed, ref, nextTick } from 'vue';
import { parseISO } from 'date-fns';
import { ChevronRight } from 'lucide-vue-next';
import { ColumnDef } from '@tanstack/vue-table';
import { DataTable, DataTableFilter } from '@/components/ui/data-table';
import { Card } from '@/components/ui/card';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { Button } from '@/components/ui/button';
import Separator from '@/components/ui/separator/Separator.vue';
import { Record, RecordForm, MultiStepFormState } from '@/types';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { useRecords, useRecordTypes } from '@/plugins/store/actions/queries/records';
import { useRecordsSearchParams } from '@/composables/records/useRecordsSearchParams';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import { Skeleton } from '@/components/ui/skeleton';
import TableCalendarModal from './TableCalendarModal.vue';
import { i18n } from '@/plugins/internationalization/i18n';


export default /*@__PURE__*/_defineComponent({
  __name: 'ActivityTable',
  props: {
    selectableRecordTypeIds: { default: () => [] },
    fetchOwnRecords: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props;

const { data: recordTypes, isLoading: isRecordTypesLoading } = useRecordTypes();
const filteredRecordTypes = computed(() => recordTypes.value?.filter((t) => props.selectableRecordTypeIds.includes(t.id)) || []);
const typeOptions = computed(() =>
    filteredRecordTypes.value.map((t) => ({
        value: t.id.toString(),
        label: t.name,
        icon_url: t.icon_url,
    }))
);

const { fetchParams, page, pageSize, pageSizeOptions, types } = useRecordsSearchParams();

const computedTypes = computed({
    get() {
        return filteredRecordTypes.value.filter((t) => fetchParams.value.typeIds?.includes(t.id)).map((t) => t.id.toString()) || [];
    },
    set(value) {
        types.value = value.map(Number);
    },
});

const filteredFetchParams = computed(() => {
    // If user has selected specific types, filter to only include those that are in selectableRecordTypeIds
    // Otherwise, use all selectableRecordTypeIds
    const selectedTypeIds = fetchParams.value.typeIds?.length > 0 ? fetchParams.value.typeIds.filter((id) => props.selectableRecordTypeIds.includes(id)) : props.selectableRecordTypeIds;

    return {
        ...fetchParams.value,
        typeIds: selectedTypeIds,
    };
});

const isQueryEnabled = computed(() => filteredFetchParams.value.typeIds.length > 0 && filteredFetchParams.value.siteIds.length > 0);
const { data: recordsResponse, isFetching: isRecordsLoading } = useRecords(filteredFetchParams, { enabled: isQueryEnabled });
const isLoading = computed(() => isRecordTypesLoading.value || isRecordsLoading.value);

const recordsData = computed(
    () =>
        recordsResponse.value?.data.map((r) => {
            const recordType = filteredRecordTypes.value.find((t) => t.id === r.type_id);
            return {
                ...r,
                name: recordType?.name,
                icon_url: recordType?.icon_url,
            };
        }) || []
);

const pagination = computed(() => ({
    totalPages: recordsResponse.value?.total_pages || 0,
    totalRows: recordsResponse.value?.total_rows || 0,
}));

const columns = computed<ColumnDef<Record & { name?: string; icon_url?: string }>[]>(() => [
    {
        header: i18n.global.t('views.activityTable.table.headers.type'),
        accessorKey: 'name',
        enableSorting: false,
    },
    {
        header: i18n.global.t('views.activityTable.table.headers.date'),
        accessorKey: 'date',
        enableSorting: false,
    },
    {
        header: i18n.global.t('views.activityTable.table.headers.amount'),
        accessorKey: 'amount',
        enableSorting: false,
    },
    {
        header: i18n.global.t('views.activityTable.table.headers.description'),
        accessorKey: 'description',
        enableSorting: false,
    },
    {
        header: '',
        accessorKey: '_actions',
    },
]);

function formatRecordDate(item: Record): Date {
    return item.date_only ? parseISO(item.date_only) : new Date(item.date);
}

const actionModalStepConfig = ref([{}, { deleteButton: true, editable: true }, { submitActionType: ActionType.PutRecordForm }, {}]);
const actionModalInitialState = ref<MultiStepFormState>({
    location: null,
    form: null,
    confirmed: false,
    farm: null,
    event: null,
    notification: null,
});
const actionModalHeader = ref<{ icon_url: string; text: string } | null>(null);

const loadingRecordId = ref<number | null>(null);

async function onRecordClicked(record: Record & { name?: string; icon_url?: string }, open: CallableFunction) {
    if (loadingRecordId.value !== null) {
        return;
    }

    try {
        loadingRecordId.value = record.id;

        const recordForm: RecordForm = await store.dispatch(ActionType.GetRecordForm, { form: { record_id: record.id } });

        actionModalInitialState.value = {
            ...actionModalInitialState.value,
            form: recordForm.form,
            record_id: record.id,
        };
        actionModalHeader.value = { icon_url: record.icon_url || '', text: record.name || '' };
        actionModalStepConfig.value[1].deleteButton = recordForm.deletable;
        actionModalStepConfig.value[1].editable = recordForm.editable;

        await nextTick();

        open();
    } finally {
        loadingRecordId.value = null;
    }
}

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Separator),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(FadeTransition, { mode: "out-in" }, {
          default: _withCtx(() => [
            (_unref(isRecordTypesLoading))
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_unref(Skeleton), { class: "w-20 h-10 bg-gray-200" })
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_unref(DataTableFilter), {
                    modelValue: computedTypes.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((computedTypes).value = $event)),
                    options: typeOptions.value,
                    title: _ctx.$t('views.activityTable.filterModal.header'),
                    "item-value": "value",
                    "item-label": "label",
                    "update-only-on-close": "",
                    align: "end"
                  }, {
                    item: _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(_component_Icon, {
                          src: item.icon_url,
                          class: "w-6 h-6"
                        }, null, 8, ["src"]),
                        _createElementVNode("div", null, _toDisplayString(item.label), 1)
                      ])
                    ]),
                    _: 1
                  }, 8, ["modelValue", "options", "title"])
                ]))
          ]),
          _: 1
        }),
        _createVNode(TableCalendarModal, { "selectable-record-type-ids": _ctx.selectableRecordTypeIds }, null, 8, ["selectable-record-type-ids"])
      ]),
      _createVNode(_unref(Card), { class: "overflow-hidden" }, {
        default: _withCtx(() => [
          _createVNode(ActionModal, {
            "initial-step-index": 1,
            "initial-state": actionModalInitialState.value,
            "step-config": actionModalStepConfig.value,
            header: actionModalHeader.value
          }, {
            default: _withCtx(({ open }) => [
              _createVNode(_unref(DataTable), {
                page: _unref(page),
                "onUpdate:page": _cache[1] || (_cache[1] = ($event: any) => (_isRef(page) ? (page).value = $event : null)),
                "page-size": _unref(pageSize),
                "onUpdate:pageSize": _cache[2] || (_cache[2] = ($event: any) => (_isRef(pageSize) ? (pageSize).value = $event : null)),
                data: recordsData.value,
                columns: columns.value,
                loading: isLoading.value,
                "page-size-options": _unref(pageSizeOptions),
                "total-pages": pagination.value.totalPages,
                "total-rows": pagination.value.totalRows,
                "server-side": ""
              }, {
                "item[name]": _withCtx(({ item }) => [
                  _createElementVNode("div", {
                    class: "flex items-center gap-2.5 transition-opacity duration-200 hover:opacity-80 cursor-pointer",
                    onClick: ($event: any) => (onRecordClicked(item, open))
                  }, [
                    (item.icon_url)
                      ? (_openBlock(), _createBlock(_component_Icon, {
                          key: 0,
                          src: item.icon_url,
                          class: "w-9 h-9"
                        }, null, 8, ["src"]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", null, _toDisplayString(item.name), 1)
                  ], 8, _hoisted_7)
                ]),
                "item[date]": _withCtx(({ item }) => [
                  _createTextVNode(_toDisplayString(_ctx.$d(formatRecordDate(item), 'short')), 1)
                ]),
                "item[amount]": _withCtx(({ item }) => [
                  _createTextVNode(_toDisplayString(item.amount), 1)
                ]),
                "item[description]": _withCtx(({ item }) => [
                  _createTextVNode(_toDisplayString(item.description), 1)
                ]),
                "item[_actions]": _withCtx(({ item }) => [
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_unref(Button), {
                      variant: "ghost",
                      size: "icon",
                      class: "hover:gray-300 rounded-full -mr-1",
                      disabled: loadingRecordId.value !== null,
                      onClick: ($event: any) => (onRecordClicked(item, open))
                    }, {
                      default: _withCtx(() => [
                        (loadingRecordId.value === item.id)
                          ? (_openBlock(), _createBlock(Spinner, {
                              key: 0,
                              class: "h-5 w-5",
                              "aria-hidden": "true"
                            }))
                          : (_openBlock(), _createBlock(_unref(ChevronRight), {
                              key: 1,
                              class: "h-5 w-5",
                              "aria-hidden": "true"
                            }))
                      ]),
                      _: 2
                    }, 1032, ["disabled", "onClick"])
                  ])
                ]),
                _: 2
              }, 1032, ["page", "page-size", "data", "columns", "loading", "page-size-options", "total-pages", "total-rows"])
            ]),
            _: 1
          }, 8, ["initial-state", "step-config", "header"])
        ]),
        _: 1
      })
    ])
  ]))
}
}

})